import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { gql } from 'graphql-request'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { twMerge } from 'tailwind-merge'
import {
  GripVertical,
  FilePlus2,
  Plus
} from 'lucide-react'

import { useMutation } from '@hooks/graphql'
import { Button } from '@components/ui/button'
import Card from '@components/Card'
import OverflowMenu from '@components/OverflowMenu'

import AddLessonModal from './AddLessonModal'
import DeleteProjectDialog from './DeleteProjectDialog'
import CourseHeader from './CourseHeader'
const UDPATE_PROJECT_MUTATION = gql`
  mutation updateEducatorProject($input: UpdateEducatorProjectInput!) {
    updateEducatorProject(input: $input) {
      educatorProject {
        id
        position
      }
    }
  }
`

const projectTheme = isDragging => {
  if (isDragging) {
    return 'bg-purple-100 shadow-lg'
  }

  return 'bg-background'
}

const ProjectItem = ({ project, index, openDeleteModal }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: project.id });

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={twMerge(
        'flex flex-row w-full p-3 items-center',
        isDragging ? 'z-10 shadow-lg opacity-90' : ''
      )}
    >
      <div
        {...attributes}
        {...listeners}
        style={{ touchAction: 'none' }}
        className='p-2 bg-background border-border border rounded-md mr-5 cursor-grab touch-none'
      >
        <GripVertical className='h-4 w-4' />
      </div>

      <Link to={`/educators/projects/${project.id}`} className='w-full text-left'>
        <h3 className='text-lg font-semibold'>{project.name}</h3>
      </Link>

      <OverflowMenu>
        <OverflowMenu.Item>
          <Link className='text-base block' to={`/educators/projects/${project.id}`}>Edit</Link>
        </OverflowMenu.Item>
        <OverflowMenu.Item>
          <button
            onClick={openDeleteModal}
            className='text-base w-full text-left text-red-500'
          >
            Delete
          </button>
        </OverflowMenu.Item>
      </OverflowMenu>
    </Card>
  );
}

const Overview = ({
  id,
  name,
  description,
  imageUrl,
  educatorProjects,
  classrooms,
  refetch
}) => {
  const [isAddLessonModalOpen, setIsAddLessonModalOpen] = useState(false)
  const [projectIdToDelete, setProjectIdToDelete] = useState(null)
  const [sortedProjects, setSortedProjects] = useState([])

  const { mutate: updateProject } = useMutation({ gqlMutation: UDPATE_PROJECT_MUTATION })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSortedProjects((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return items;

        const newItems = arrayMove(items, oldIndex, newIndex);
        updateProject({ input: { id: active.id, position: newIndex } });
        return newItems;
      });
    }
  };

  useEffect(() => {
    setSortedProjects(educatorProjects || []);
  }, [educatorProjects])

  return (
    <>
      <div className='w-full py-8 px-5'>
        <div className='max-w-3xl mx-auto'>
          <div className='flex flex-col gap-6'>
            <div>
              <CourseHeader
                courseId={id}
                name={name}
                description={description}
                imageUrl={imageUrl}
              />
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-3 sm:ml-0 ml-3'>Lessons</h3>

              <Choose>
                <When condition={sortedProjects.length > 0}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={sortedProjects.map(p => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {sortedProjects.map((project, index) => (
                          <ProjectItem
                            key={project.id}
                            project={project}
                            index={index}
                            openDeleteModal={() => setProjectIdToDelete(project.id)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  <div className='flex justify-center'>
                    <Button
                      variant="outline"
                      onClick={() => setIsAddLessonModalOpen(true)}
                      className='mt-2 border-dashed mx-auto'
                    >
                      <Plus className='size-5' />
                      Add New Lesson
                    </Button>
                  </div>
                </When>

                <Otherwise>
                  <button
                    type='button'
                    onClick={() => setIsAddLessonModalOpen(true)}
                    className='relative block w-full rounded-lg border-2 border-dashed border-border p-12 text-center hover:border-primary focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                  >
                    <FilePlus2 size={24} className='mx-auto' />
                    <span className='mt-2 block text-sm text-primary'>Add a new lesson</span>
                  </button>
                </Otherwise>
              </Choose>
            </div>


          </div>
        </div>
      </div>

      <DeleteProjectDialog
        projectId={projectIdToDelete}
        isOpen={!!projectIdToDelete}
        closeModal={() => setProjectIdToDelete(null)}
        refetch={refetch}
        assignedToClassrooms={classrooms?.length > 0}
        name={sortedProjects.find(project => project.id === projectIdToDelete)?.name}
      />

      <AddLessonModal
        id={id}
        seriesName={name}
        isOpen={isAddLessonModalOpen}
        onClose={() => setIsAddLessonModalOpen(false)}
      />

    </>
  )
}

export default Overview
